﻿import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { observer } from "mobx-react-lite";
import {
  Building2,
  CalendarClock,
  LayoutPanelLeft,
  Goal,
  ChevronDown,
  CheckCircle2,
  Files,
} from "lucide-react";

import { useTran } from "@/hooks";
import { PageHeader, SelectField, FormField } from "@/components/common";
import { Button } from "@/components/ui/button";
import {
  ORG_BOUNDARY,
  GHG_REPORT_PURPOSE,
  REPORT_STANDARD,
  MATERIALITY_ASSESSMENT_CRITERIA,
  SCOPE_CATEGORY,
  YEAR_OPTIONS,
  LEVEL_ASSURANCE,
} from "@/lib/constants";

import { useOnboardingStore } from "../hooks";
import {
  calculateNxmapProgress,
  getBaseYearEndBounds,
  getBaseYearEndHelperText,
  getBaseYearDateRange,
  getBaseYearStartBounds,
  getBaseYearStartHelperText,
  getDefaultBaseYearDates,
  getReportYearEndBounds,
  getReportYearEndHelperText,
  getReportYearDateRange,
  getReportYearStartBounds,
  getReportYearStartHelperText,
  getDefaultReportYearDates,
  normalizeNxmapFormValue,
  toDateInput,
} from "../utils";
import { validateNxmapForm } from "../schemas";
import MeetingRequestDialog from "./MeetingRequestDialog";
import OnboardingWizardShell from "./OnboardingWizardShell";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

const toYearNumber = (value) => {
  if (value === "" || value === null || typeof value === "undefined")
    return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const NxmapForm = observer(
  ({
    showHeaderActions = false,
    submitTick,
    onValid,
    prevTick,
    skipTick,
    setNxmapStep,
    form_disabled,
  }) => {
    const { t } = useTran();
    const {
      onboardData,
      baseYearOptions,
      currentYear,
      getYearOptions,
      reportYearOptions,
      getReportYearOptions,
    } = useOnboardingStore();

    const [form, setForm] = useState({
      organizationBoundary: "",
      ghgReportPurpose: "",
      threshold: "",
      reportingStandard: "",
      baseYear: "",
      baseYearStart: "",
      baseYearEnd: "",
      // assurance: "",
      reportingYear: "",
      reportingPeriodStart: "",
      reportingPeriodEnd: "",
    });
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [meetingOpen, setMeetingOpen] = useState(false);
    const [step, setStep] = useState(1);
    const [expandedRows, setExpandedRows] = useState({});
    const [rowDetails, setRowDetails] = useState({});
    const [completedSteps, setCompletedSteps] = useState({
      1: false,
      2: false,
      3: false,
      4: false,
    });
    const totalSteps = 4;

    const CRITERION_FIELD_MAP = useMemo(
      () => ({
        // map criterion key -> rowDetails field name
        reqByLaw: "reqByLaw",
        data_credibility: "dataCredibility",
        emission_factor_sources: "emissionFactorSource",
        emission_reduction_technology_implementation_feasibility:
          "technologyFeasibility",
        occurrence_frequency: "occurrenceFrequency",
        emission_volume: "emissionVolume",
      }),
      [],
    );
    const CRITERION_KEYS = useMemo(
      () => MATERIALITY_ASSESSMENT_CRITERIA.map((c) => c.key),
      [],
    );

    const getDefaultRowDetail = useCallback(() => {
      return {
        reqByLaw: true,
        dataCredibility: 1,
        emissionFactorSource: 1,
        technologyFeasibility: 1,
        occurrenceFrequency: 1,
        emissionVolume: 1,
        targetYear: toYearNumber(YEAR_OPTIONS[0]?.key),
      };
    }, []);

    const normalizeRowDetails = useCallback(
      (seed = {}) => {
        const next = {};
        SCOPE_CATEGORY.forEach((category) => {
          const existing = seed?.[category.key];
          next[category.key] = existing
            ? { ...existing }
            : getDefaultRowDetail();
        });
        return next;
      },
      [getDefaultRowDetail],
    );

    const getOptionScore = useCallback((criterionKey, optionKey) => {
      if (typeof optionKey === "number") return optionKey;
      const numericScore = Number(optionKey);
      if (!Number.isNaN(numericScore)) return numericScore;
      const criterion = MATERIALITY_ASSESSMENT_CRITERIA.find(
        (item) => item.key === criterionKey,
      );
      if (!criterion) return 0;
      const opt = (criterion.options || []).find((o) => o.key === optionKey);
      return opt?.score ?? 0;
    }, []);

    const calculateRowScore = useCallback(
      (detail) =>
        CRITERION_KEYS.reduce((sum, criterionKey) => {
          const fieldKey = CRITERION_FIELD_MAP[criterionKey];
          if (!fieldKey) return sum;
          const selected = detail?.[fieldKey];
          return sum + getOptionScore(criterionKey, selected);
        }, 0),
      [CRITERION_FIELD_MAP, CRITERION_KEYS, getOptionScore],
    );

    useEffect(() => {
      setRowDetails((prev) => {
        return normalizeRowDetails(prev);
      });
    }, [normalizeRowDetails]);

    useEffect(() => {
      getYearOptions?.();
      getReportYearOptions();
    }, [getYearOptions, getReportYearOptions]);

    useEffect(() => {
      if (!onboardData) return;
      setForm({
        organizationBoundary:
          onboardData.onboard?.organizationBoundary ??
          onboardData.nxmap?.organizationBoundary ??
          "",
        baseYear: String(
          onboardData.onboard?.baseYear ?? onboardData.nxmap?.baseYear ?? "",
        ),
        reportingStandard:
          onboardData.onboard?.reportingStandard ??
          onboardData.nxmap?.reportingStandard ??
          "",
        ghgReportPurpose: onboardData.nxmap?.ghgReportPurpose ?? "",
        baseYearStart: normalizeNxmapFormValue(
          "baseYearStart",
          onboardData.nxmap?.baseYearStart,
        ),
        baseYearEnd: normalizeNxmapFormValue(
          "baseYearEnd",
          onboardData.nxmap?.baseYearEnd,
        ),
        threshold: String(onboardData.nxmap?.threshold ?? ""),
        // assurance: onboardData.nxmap?.assurance ?? "",
        reportingYear: String(
          onboardData.onboard?.reportingYear ??
            onboardData.nxmap?.reportingYear ??
            "",
        ),
        reportingPeriodStart: normalizeNxmapFormValue(
          "reportingPeriodStart",
          onboardData.nxmap?.reportingPeriodStart,
        ),
        reportingPeriodEnd: normalizeNxmapFormValue(
          "reportingPeriodEnd",
          onboardData.nxmap?.reportingPeriodEnd,
        ),
      });
      const savedMateriality = onboardData.nxmap?.materialityAssessment;
      if (savedMateriality) {
        setRowDetails((prev) => {
          const next = normalizeRowDetails(prev);
          SCOPE_CATEGORY.forEach((category) => {
            const fromArray =
              Array.isArray(savedMateriality) &&
              savedMateriality.find?.(
                (item) => item?.categoryKey === category.key,
              );
            const saved = savedMateriality?.[category.key] || fromArray;
            if (saved) {
              next[category.key] = { ...getDefaultRowDetail(), ...saved };
            }
          });
          return next;
        });
      }
    }, [getDefaultRowDetail, normalizeRowDetails, onboardData]);

    useEffect(() => {
      const savedProgress = Number(onboardData?.nxmap?.progress);
      if (!Number.isFinite(savedProgress)) return;
      const clamped = Math.max(0, Math.min(100, savedProgress));
      const completedCount = Math.round((clamped / 100) * totalSteps);
      setCompletedSteps((prev) => {
        const currentCount = Object.values(prev || {}).filter(Boolean).length;
        if (currentCount >= completedCount) return prev;
        return {
          1: completedCount >= 1,
          2: completedCount >= 2,
          3: completedCount >= 3,
          4: completedCount >= 4,
        };
      });
    }, [onboardData?.nxmap?.progress, totalSteps]);

    const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));
    const markTouched = (key) =>
      setTouched((current) => ({ ...current, [key]: true }));
    const handleBlur = (key) => () => markTouched(key);

    const baseYearDateRange = useMemo(
      () => getBaseYearDateRange(form.baseYear),
      [form.baseYear],
    );
    const baseYearStartBounds = useMemo(
      () => getBaseYearStartBounds(form.baseYear),
      [form.baseYear],
    );
    const baseYearEndBounds = useMemo(
      () => getBaseYearEndBounds(form.baseYear, form.baseYearStart),
      [form.baseYear, form.baseYearStart],
    );
    const baseYearStartHelperText = useMemo(
      () => getBaseYearStartHelperText(form.baseYear),
      [form.baseYear],
    );
    const baseYearEndHelperText = useMemo(
      () => getBaseYearEndHelperText(baseYearEndBounds, form.baseYear),
      [baseYearEndBounds, form.baseYear],
    );
    const reportingYearOptions = useMemo(() => {
      const base = form.baseYear ? Number(form.baseYear) : null;
      if (base && currentYear) {
        const range = [];
        for (let year = base; year <= currentYear; year += 1) range.push(year);
        return range;
      }
      return baseYearOptions || reportYearOptions || [];
    }, [form.baseYear, currentYear, baseYearOptions, reportYearOptions]);
    const hasValidReportingYear = useMemo(
      () =>
        Boolean(form.reportingYear) &&
        reportingYearOptions.some(
          (year) => String(year) === String(form.reportingYear),
        ),
      [form.reportingYear, reportingYearOptions],
    );
    const activeReportingYear = hasValidReportingYear ? form.reportingYear : "";
    const reportYearDateRange = useMemo(
      () => getReportYearDateRange(activeReportingYear),
      [activeReportingYear],
    );
    const reportYearStartBounds = useMemo(
      () => getReportYearStartBounds(activeReportingYear),
      [activeReportingYear],
    );
    const reportYearEndBounds = useMemo(
      () =>
        getReportYearEndBounds(activeReportingYear, form.reportingPeriodStart),
      [activeReportingYear, form.reportingPeriodStart],
    );
    const reportYearStartHelperText = useMemo(
      () => getReportYearStartHelperText(activeReportingYear),
      [activeReportingYear],
    );
    const reportYearEndHelperText = useMemo(
      () =>
        getReportYearEndHelperText(reportYearEndBounds, activeReportingYear),
      [reportYearEndBounds, activeReportingYear],
    );

    const handleThresholdChange = (event) => {
      const numericValue = event.target.value.replace(/[^\d]/g, "");
      update({ threshold: numericValue });
    };

    const progress = useMemo(
      () => calculateNxmapProgress({ form, rowDetails }),
      [form, rowDetails],
    );
    const pct = Math.max(0, Math.min(100, Number(progress) || 0));

    const organizationBoundaryOptions = useMemo(
      () =>
        ORG_BOUNDARY.map((item) => ({
          ...item,
          label: t(item.value),
        })),
      [t],
    );
    const ghgPurposeOptions = useMemo(
      () =>
        GHG_REPORT_PURPOSE.map((item) => ({
          ...item,
          label: t(item.value),
        })),
      [t],
    );
    const reportingStandardOptions = useMemo(
      () =>
        REPORT_STANDARD.map((item) => ({
          ...item,
          label: t(item.value),
        })),
      [t],
    );

    // const assuranceOptions = useMemo(
    //   () =>
    //     LEVEL_ASSURANCE.map((item) => ({
    //       ...item,
    //       label: t(item.value),
    //     })),
    //   [t],
    // );

    const handleBaseYearChange = useCallback((year) => {
      setForm((prev) => {
        if (!year) {
          return { ...prev, baseYear: "", baseYearStart: "", baseYearEnd: "" };
        }
        const { start, end } = getDefaultBaseYearDates(year);
        return {
          ...prev,
          baseYear: year,
          baseYearStart: start,
          baseYearEnd: end,
        };
      });
    }, []);

    const handleReportYearChange = useCallback((year) => {
      setForm((prev) => {
        if (!year) {
          return {
            ...prev,
            reportingYear: "",
            reportingPeriodStart: "",
            reportingPeriodEnd: "",
          };
        }
        const { start, end } = getDefaultReportYearDates(year);
        return {
          ...prev,
          reportingYear: year,
          reportingPeriodStart: start,
          reportingPeriodEnd: end,
        };
      });
    }, []);

    const handleBaseYearStartChange = useCallback((value) => {
      setForm((prev) => {
        const normalizedValue = toDateInput(value);
        const next = { ...prev, baseYearStart: normalizedValue };
        if (!normalizedValue) return next;

        const { min: minStart, max: maxStart } = getBaseYearStartBounds(
          prev.baseYear,
        );

        if (minStart && next.baseYearStart < minStart) {
          next.baseYearStart = minStart;
        } else if (maxStart && next.baseYearStart > maxStart) {
          next.baseYearStart = maxStart;
        }

        const { min: minAllowed, max: maxAllowed } = getBaseYearEndBounds(
          prev.baseYear,
          next.baseYearStart,
        );

        if (
          !prev.baseYearEnd ||
          (minAllowed && prev.baseYearEnd < minAllowed) ||
          (maxAllowed && prev.baseYearEnd > maxAllowed)
        ) {
          next.baseYearEnd = maxAllowed || "";
        }
        return next;
      });
    }, []);

    const handleReportPeriodStartChange = useCallback((value) => {
      setForm((prev) => {
        const normalizedValue = toDateInput(value);
        const next = { ...prev, reportingPeriodStart: normalizedValue };
        if (!normalizedValue) return next;

        const { min: minStart, max: maxStart } = getReportYearStartBounds(
          prev.reportingYear,
        );

        if (minStart && next.reportingPeriodStart < minStart) {
          next.reportingPeriodStart = minStart;
        } else if (maxStart && next.reportingPeriodStart > maxStart) {
          next.reportingPeriodStart = maxStart;
        }

        const { min: minAllowed, max: maxAllowed } = getReportYearEndBounds(
          prev.reportingYear,
          next.reportingPeriodStart,
        );

        if (
          !prev.reportingPeriodEnd ||
          (minAllowed && prev.reportingPeriodEnd < minAllowed) ||
          (maxAllowed && prev.reportingPeriodEnd > maxAllowed)
        ) {
          next.reportingPeriodEnd = maxAllowed || "";
        }
        return next;
      });
    }, []);

    const buildDetailErrorKey = useCallback(
      (categoryKey, fieldKey) => `detail-${categoryKey}-${fieldKey}`,
      [],
    );

    const getFieldError = useCallback(
      (key) => (touched[key] || submitTick > 0 ? errors[key] : undefined),
      [errors, submitTick, touched],
    );

    const getDetailFieldError = useCallback(
      (categoryKey, fieldKey) => {
        return getFieldError(buildDetailErrorKey(categoryKey, fieldKey));
      },
      [buildDetailErrorKey, getFieldError],
    );
    const touchErrors = (errs) => {
      if (!errs || Object.keys(errs).length === 0) return;
      setTouched((prev) =>
        Object.keys(errs).reduce((acc, key) => ({ ...acc, [key]: true }), {
          ...prev,
        }),
      );
    };

    const validateDetailFields = useCallback(() => {
      const detailErrors = {};
      const categoriesWithErrors = new Set();
      const message = (text) => (typeof t === "function" ? t(text) : text);

      SCOPE_CATEGORY.forEach((category) => {
        const detail = rowDetails[category.key] || getDefaultRowDetail();
        const isNonCustom =
          detail.reqByLaw === false || detail.reqByLaw === "false";
        if (isNonCustom) {
          MATERIALITY_ASSESSMENT_CRITERIA.forEach((criterion) => {
            const fieldKey = CRITERION_FIELD_MAP[criterion.key];
            if (!fieldKey) return;
            const value = detail[fieldKey];
            const isMissing =
              value === "" || value === null || typeof value === "undefined";
            if (isMissing) {
              const labelText =
                typeof t === "function" ? t(criterion.label) : criterion.label;
              detailErrors[buildDetailErrorKey(category.key, fieldKey)] =
                message(`Select ${labelText}`);
              categoriesWithErrors.add(category.key);
            }
          });
        }
      });

      return { detailErrors, categoriesWithErrors };
    }, [
      CRITERION_FIELD_MAP,
      buildDetailErrorKey,
      getDefaultRowDetail,
      rowDetails,
      t,
    ]);

    const runValidation = (targetSTep = step) => {
      const baseErrors = validateNxmapForm(
        form,
        baseYearEndBounds,
        t,
        reportYearEndBounds,
        { step: targetSTep },
      );

      let mergedErrors = { ...baseErrors };
      let categoriesWithDetailErrors = new Set();

      if (targetSTep >= 3) {
        const { detailErrors, categoriesWithErrors } = validateDetailFields();
        mergedErrors = { ...mergedErrors, ...detailErrors };
        categoriesWithDetailErrors = categoriesWithErrors;
      }

      setErrors(mergedErrors);
      touchErrors(mergedErrors);

      if (targetSTep >= 3 && categoriesWithDetailErrors.size > 0) {
        setExpandedRows((prev) => {
          let changed = false;
          const next = { ...prev };
          SCOPE_CATEGORY.forEach((category) => {
            if (
              categoriesWithDetailErrors.has(category.key) &&
              !next[category.key]
            ) {
              next[category.key] = true;
              changed = true;
            }
          });
          return changed ? next : prev;
        });
      }

      const ok = Object.keys(mergedErrors).length === 0;
      return ok;
    };

    const errorSummary = useMemo(() => {
      if (submitTick <= 0) return [];
      const messages = Object.values(errors).filter(
        (msg) => typeof msg === "string" && msg.trim().length > 0,
      );
      return Array.from(new Set(messages));
    }, [errors, submitTick]);

    useEffect(() => {
      if (!form.baseYear || !form.baseYearStart) return;
      const { min, max } = baseYearStartBounds || {};
      if (min && form.baseYearStart < min) {
        update({ baseYearStart: min });
      } else if (max && form.baseYearStart > max) {
        update({ baseYearStart: max });
      }
    }, [form.baseYear, form.baseYearStart, baseYearStartBounds]);

    useEffect(() => {
      if (!activeReportingYear || !form.reportingPeriodStart) return;
      const { min, max } = getReportYearStartBounds(activeReportingYear) || {};
      if (min && form.reportingPeriodStart < min) {
        update({ reportingPeriodStart: min });
      } else if (max && form.reportingPeriodStart > max) {
        update({ reportingPeriodStart: max });
      }
    }, [activeReportingYear, form.reportingPeriodStart]);

    useEffect(() => {
      if (!form.reportingYear) return;
      if (hasValidReportingYear) return;
      update({
        reportingPeriodStart: "",
        reportingPeriodEnd: "",
      });
    }, [form.reportingYear, hasValidReportingYear]);

    useEffect(() => {
      if (!form.baseYearEnd) return;
      const { min, max } = baseYearEndBounds || {};
      if (min && form.baseYearEnd < min) {
        update({ baseYearEnd: min });
      } else if (max && form.baseYearEnd > max) {
        update({ baseYearEnd: max });
      }
    }, [form.baseYearEnd, baseYearEndBounds]);

    useEffect(() => {
      if (!form.reportingPeriodEnd) return;
      const { min, max } = reportYearEndBounds || {};
      if (min && form.reportingPeriodEnd < min) {
        update({ reportingPeriodEnd: min });
      } else if (max && form.reportingPeriodEnd > max) {
        update({ reportingPeriodEnd: max });
      }
    }, [form.reportingPeriodEnd, reportYearEndBounds]);

    useEffect(() => {
      if (submitTick <= 0) return;
      if (step === 1) {
        if (runValidation(1)) {
          setCompletedSteps((prev) => ({ ...prev, 1: true }));
          setStep(2);
        }
        return;
      }
      if (step === 2) {
        if (runValidation(2)) {
          setCompletedSteps((prev) => ({ ...prev, 2: true }));
          setStep(3);
        }
        return;
      }
      if (step === 3) {
        if (runValidation(3)) {
          setCompletedSteps((prev) => ({ ...prev, 3: true }));
          setStep(4);
        }
        return;
      }
      if (step !== 4) return;
      if (!runValidation(4)) return;
      if (runValidation(4)) {
        const nextCompleted = { ...completedSteps, 4: true };
        setCompletedSteps(nextCompleted);
      }
      const nextProgress = calculateNxmapProgress({ form, rowDetails });
      const materialityAssessment = SCOPE_CATEGORY.map((category) => {
        const detail = rowDetails[category.key] || getDefaultRowDetail();
        const isReqByLaw =
          detail.reqByLaw === true || detail.reqByLaw === "true";
        const total = isReqByLaw ? 5 : calculateRowScore(detail);

        return {
          categoryKey: category.key,
          categoryName: category.category,
          ...detail,
          total,
        };
      });
      onValid?.({
        project: "nxmap",
        progress: nextProgress,
        materialityAssessment,
        ...form,
      });
    }, [submitTick]);

    useEffect(() => {
      setNxmapStep?.(step);
    }, [step, setNxmapStep]);

    useEffect(() => {
      if (prevTick > 0) {
        setStep((s) => (s > 1 ? s - 1 : s));
      }
    }, [prevTick]);

    useEffect(() => {
      if (skipTick > 0) {
        setStep((s) => (s < 4 ? s + 1 : s));
      }
    }, [skipTick]);

    const toggleRow = (categoryKey) => {
      setExpandedRows((prev) => ({
        ...prev,
        [categoryKey]: !prev[categoryKey],
      }));
    };

    const updateRowDetail = useCallback(
      (categoryKey, criterionKey, value) => {
        const field = CRITERION_FIELD_MAP[criterionKey];
        if (!field) return;
        const normalizedValue =
          field === "reqByLaw" ? value === true || value === "true" : value;
        setRowDetails((prev) => {
          const next = normalizeRowDetails(prev);
          next[categoryKey] = {
            ...next[categoryKey],
            [field]: normalizedValue,
          };
          return next;
        });
      },
      [CRITERION_FIELD_MAP, normalizeRowDetails],
    );

    const handleReqByLawChange = useCallback(
      (categoryKey, value) => {
        const normalizedValue = value === true || value === "true";
        setRowDetails((prev) => {
          const next = normalizeRowDetails(prev);
          next[categoryKey] = {
            ...next[categoryKey],
            reqByLaw: normalizedValue,
          };
          return next;
        });
      },
      [normalizeRowDetails],
    );

    const handleDisclosureYearChange = useCallback(
      (categoryKey, year) => {
        setRowDetails((prev) => {
          const next = normalizeRowDetails(prev);
          next[categoryKey] = {
            ...next[categoryKey],
            targetYear: toYearNumber(year),
          };
          return next;
        });
      },
      [normalizeRowDetails],
    );

    const progressFooter = (
      <div className="flex items-center gap-3">
        <div className="h-1.5 w-full flex-1 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full bg-gradient-to-r from-emerald-400 to-blue-500 transition-all duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="text-sm font-semibold text-foreground">{pct}%</span>
      </div>
    );

    const headerActions = showHeaderActions ? (
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setMeetingOpen(true)}
          title={t("Request a meeting")}
          className="gap-2 rounded-full"
        >
          <span>{t("Request Meeting")}</span>
          <span className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-muted text-foreground">
            <CalendarClock className="h-4 w-4" />
          </span>
        </Button>
      </div>
    ) : null;

    const navSteps = [
      { id: 1, title: t("Organizational Boundary"), icon: Building2 },
      {
        id: 2,
        title: t("Reporting and Base Year Details"),
        icon: Files,
      },
      {
        id: 3,
        title: t("Materiality Assessment Criteria"),
        icon: LayoutPanelLeft,
      },
      {
        id: 4,
        title: t("Materiality Assessment and Reporting Boundary"),
        icon: Goal,
      },
    ];
    return (
      <div className="flex flex-col gap-6 lg:flex-1 lg:min-h-0">
        <PageHeader
          title={t("NXMap - Boundary Settings")}
          description={t(
            "Configure reporting boundaries, purposes, and base-year dates.",
          )}
          actions={headerActions}
        />
        {progressFooter}

        <OnboardingWizardShell
          navItems={navSteps.map((stepItem) => ({
            ...stepItem,
            isActive: stepItem.id === step,
            isDone: stepItem.id < step,
            onClick: () =>
              setStep((current) =>
                progress === 100
                  ? stepItem.id
                  : stepItem.id < current
                    ? stepItem.id
                    : current,
              ),
          }))}
        >
          <MeetingRequestDialog
            open={meetingOpen}
            onOpenChange={setMeetingOpen}
          />
          {errorSummary.length > 0 && (
            <div className="mb-4 rounded-radius border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <p className="font-medium">
                {t("Please fix the highlighted fields to continue.")}
              </p>
              <ul className="mt-1 list-disc pl-5">
                {errorSummary.slice(0, 10).map((msg) => (
                  <li key={msg}>{msg}</li>
                ))}
              </ul>
            </div>
          )}
          {step === 1 && (
            <div className="grid grid-cols-1 gap-6 p-2 md:grid-cols-2">
              <div className="grid grid-cols-1 gap-6 md:col-span-2 md:grid-cols-2">
                <div>
                  <SelectField
                    id="organizationBoundary"
                    label={t("What is your organization boundary?")}
                    placeholder={t("Select boundary type")}
                    value={form.organizationBoundary}
                    onValueChange={(value) =>
                      update({ organizationBoundary: value })
                    }
                    onBlur={handleBlur("organizationBoundary")}
                    options={organizationBoundaryOptions}
                    error={getFieldError("organizationBoundary")}
                    required
                    disabled={form_disabled}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t(
                      "Choose how your organization consolidates emissions (e.g., operational control).",
                    )}
                  </p>
                </div>
                <div>
                  <FormField
                    id="threshold"
                    label={t("What is your disclosure threshold?")}
                    placeholder={t("Enter threshold value")}
                    value={form.threshold}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={2}
                    onChange={handleThresholdChange}
                    onBlur={handleBlur("threshold")}
                    error={getFieldError("threshold")}
                    disabled={form_disabled}
                    required
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("Minimum emissions level to disclose between 3 to 25.")}
                  </p>
                </div>
                <div>
                  <SelectField
                    id="ghgReportPurpose"
                    label={t("What is the purpose of GHG reporting?")}
                    placeholder={t("Select purpose")}
                    value={form.ghgReportPurpose}
                    onValueChange={(value) =>
                      update({ ghgReportPurpose: value })
                    }
                    onBlur={handleBlur("ghgReportPurpose")}
                    options={ghgPurposeOptions}
                    error={getFieldError("ghgReportPurpose")}
                    disabled={form_disabled}
                    required
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t(
                      "Select the main objective for preparing your GHG inventory.",
                    )}
                  </p>
                </div>
                <div>
                  <SelectField
                    id="reportingStandard"
                    label={t("What is your reporting standard?")}
                    placeholder={t("Select reporting standard")}
                    value={form.reportingStandard}
                    onValueChange={(value) =>
                      update({ reportingStandard: value })
                    }
                    onBlur={handleBlur("reportingStandard")}
                    options={reportingStandardOptions}
                    error={getFieldError("reportingStandard")}
                    disabled={form_disabled}
                    required
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t(
                      "Choose how your reporting standard (e.g., ISO 14064-1).",
                    )}
                  </p>
                </div>
                {/* {(form.ghgReportPurpose === "compliance" ||
                form.ghgReportPurpose === "stakeholders_requirement") && (
                <>
                  <SelectField
                    id="assurance"
                    label={t("What is the targeted level of assurance?")}
                    placeholder={t("Select the targeted level of assurance")}
                    value={form.assurance}
                    onValueChange={(value) => update({ assurance: value })}
                    onBlur={handleBlur("assurance")}
                    options={assuranceOptions}
                    error={getFieldError("assurance")}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t(
                      "Select the targeted level of assurance for preparing your GHG inventory.",
                    )}
                  </p>
                </>
              )} */}
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-6">
                  <div>
                    <SelectField
                      id="baseYear"
                      label={t("What is the base year?")}
                      placeholder={t("Select base year")}
                      value={form.baseYear}
                      onValueChange={handleBaseYearChange}
                      onBlur={handleBlur("baseYear")}
                      options={(baseYearOptions || []).map((year) => ({
                        key: String(year),
                        label: year,
                      }))}
                      error={getFieldError("baseYear")}
                      disabled={form_disabled}
                      required
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t(
                        "Baseline year used for tracking emissions reductions.",
                      )}
                    </p>
                  </div>
                  <div>
                    <FormField
                      id="baseYearStart"
                      label={t("What is the base year period start?")}
                      type="date"
                      value={form.baseYearStart}
                      min={baseYearStartBounds.min || undefined}
                      max={baseYearStartBounds.max || undefined}
                      onChange={(event) =>
                        handleBaseYearStartChange(event.target.value)
                      }
                      onBlur={handleBlur("baseYearStart")}
                      error={getFieldError("baseYearStart")}
                      disabled={form_disabled || !form.baseYear}
                      required
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      {baseYearStartHelperText}
                    </p>
                  </div>
                  <div>
                    <FormField
                      id="baseYearEnd"
                      label={t("What is the base year period end?")}
                      type="date"
                      value={form.baseYearEnd}
                      min={baseYearEndBounds.min || undefined}
                      max={baseYearDateRange.end || undefined}
                      onChange={(event) =>
                        update({ baseYearEnd: event.target.value })
                      }
                      onBlur={handleBlur("baseYearEnd")}
                      error={getFieldError("baseYearEnd")}
                      disabled={
                        form_disabled || !form.baseYear || !form.baseYearStart
                      }
                      required
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      {baseYearEndHelperText}
                    </p>
                  </div>
                </div>
                <div className="space-y-6">
                  <div>
                    <SelectField
                      id="reportingYear"
                      label={t("What is the report year?")}
                      placeholder={t("Select report year")}
                      value={form.reportingYear}
                      onValueChange={handleReportYearChange}
                      onBlur={handleBlur("reportingYear")}
                      options={reportingYearOptions.map((year) => ({
                        key: String(year),
                        label: year,
                      }))}
                      error={getFieldError("reportingYear")}
                      disabled={form_disabled}
                      required
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t(
                        "Reporting year used for tracking emissions reductions.",
                      )}
                    </p>
                  </div>
                  <div>
                    <FormField
                      id="reportingPeriodStart"
                      label={t("What is the report year period start?")}
                      type="date"
                      value={form.reportingPeriodStart}
                      min={
                        hasValidReportingYear
                          ? reportYearStartBounds.min || undefined
                          : undefined
                      }
                      max={
                        hasValidReportingYear
                          ? reportYearStartBounds.max || undefined
                          : undefined
                      }
                      onChange={(event) =>
                        handleReportPeriodStartChange(event.target.value)
                      }
                      onBlur={handleBlur("reportingPeriodStart")}
                      error={getFieldError("reportingPeriodStart")}
                      disabled={form_disabled || !hasValidReportingYear}
                      required
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      {reportYearStartHelperText}
                    </p>
                  </div>
                  <div>
                    <FormField
                      id="reportingPeriodEnd"
                      label={t("What is the report year period end?")}
                      type="date"
                      value={form.reportingPeriodEnd}
                      min={
                        hasValidReportingYear
                          ? reportYearEndBounds.min || undefined
                          : undefined
                      }
                      max={
                        hasValidReportingYear
                          ? reportYearDateRange.end || undefined
                          : undefined
                      }
                      onChange={(event) =>
                        update({ reportingPeriodEnd: event.target.value })
                      }
                      onBlur={handleBlur("reportingPeriodEnd")}
                      error={getFieldError("reportingPeriodEnd")}
                      disabled={
                        form_disabled ||
                        !hasValidReportingYear ||
                        !form.reportingPeriodStart
                      }
                      required
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      {reportYearEndHelperText}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="overflow-x-auto rounded-radius border bg-card">
              <table className="w-full min-w-[720px] border-collapse text-sm text-foreground">
                <thead className="z-20 bg-muted text-left font-semibold">
                  <tr>
                    <th className="px-4 py-3 rounded-tl-xl">
                      {t("Evaluation Factor")}
                    </th>
                    <th className="px-4 py-3">{t("Scoring Item")}</th>
                    <th className="px-4 py-3 text-right rounded-tr-xl">
                      {t("Score")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {MATERIALITY_ASSESSMENT_CRITERIA.map((criterion) =>
                    criterion.options.map((option, index) => (
                      <tr
                        key={`${criterion.key}-${option.key}`}
                        className="align-top"
                      >
                        {index === 0 && (
                          <td
                            rowSpan={criterion.options.length}
                            className="bg-muted/10 px-4 py-3 font-semibold text-foreground/90"
                          >
                            {t(criterion.label)}
                          </td>
                        )}
                        <td className="px-4 py-3 text-foreground/90">
                          {t(option.label)}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold">
                          {option.score}
                        </td>
                      </tr>
                    )),
                  )}
                </tbody>
              </table>
            </div>
          )}
          {step === 4 && (
            <div className="overflow-x-auto rounded-radius border bg-card">
              <table className="w-full min-w-[720px] border-collapse text-sm text-foreground">
                <thead className="z-20 bg-muted text-left font-semibold">
                  <tr>
                    <th className="px-4 py-3 rounded-tl-xl">{t("Category")}</th>
                    <th className="px-4 py-3">
                      {t("Required by Law/Supply/Chain/Self Requirement")}
                    </th>
                    <th className="px-4 py-3">{t("Score")}</th>
                    <th className="px-4 py-3">{t("Disclosure Required")}</th>
                    <th className="px-4 py-3">{t("Disclosure Target Year")}</th>
                    <th className="px-4 py-3 text-right rounded-tr-xl">{""}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {SCOPE_CATEGORY.map((category) => {
                    const isExpanded = Boolean(expandedRows[category.key]);
                    const detail =
                      rowDetails[category.key] || getDefaultRowDetail();
                    const reqByLawCheck =
                      detail.reqByLaw === true || detail.reqByLaw === "true";
                    const isNonReqByLaw =
                      detail.reqByLaw === false || detail.reqByLaw === "false";
                    const rowScore = reqByLawCheck
                      ? 5
                      : calculateRowScore(detail);
                    return (
                      <Fragment key={category.key}>
                        <tr>
                          <td className="px-3 py-2 align-top">
                            {t(category.value)}
                          </td>
                          <td>
                            <RadioGroup
                              name={`reqByLaw-${category.key}`}
                              value={String(detail.reqByLaw)}
                              onValueChange={(value) =>
                                handleReqByLawChange(category.key, value)
                              }
                              className="flex flex-wrap items-center gap-4"
                              disabled={form_disabled}
                            >
                              {["yes", "no"].map((value) => {
                                const boolString =
                                  value === "yes" ? "true" : "false";
                                return (
                                  <div
                                    key={value}
                                    className="flex items-center gap-2"
                                  >
                                    <RadioGroupItem
                                      id={`reqByLaw-${category.key}-${value}`}
                                      value={boolString}
                                    />
                                    <Label
                                      htmlFor={`reqByLaw-${category.key}-${value}`}
                                      className="uppercase"
                                    >
                                      {value}
                                    </Label>
                                  </div>
                                );
                              })}
                            </RadioGroup>
                          </td>
                          <td className="p-3 text-center font-semibold">
                            {rowScore}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center justify-center px-3">
                              {(form.threshold && rowScore >= form.threshold) ||
                              detail.reqByLaw === true ? (
                                <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                              ) : null}
                            </div>
                          </td>
                          <td className="p-2">
                            <SelectField
                              id={`targetYear-${category.key}`}
                              options={YEAR_OPTIONS}
                              value={
                                detail.targetYear !== null &&
                                typeof detail.targetYear !== "undefined"
                                  ? String(detail.targetYear)
                                  : ""
                              }
                              onValueChange={(value) =>
                                handleDisclosureYearChange(category.key, value)
                              }
                              placeholder={t("Select year")}
                              onBlur={handleBlur("targetYear")}
                              error={getFieldError("targetYear")}
                              required
                              disabled={form_disabled}
                            />
                          </td>
                          {isNonReqByLaw && (
                            <td className="px-2 py-2 text-right">
                              <button
                                type="button"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background/40 hover:bg-background/70 transition disabled:opacity-50"
                                onClick={() =>
                                  isNonReqByLaw && toggleRow(category.key)
                                }
                                aria-label="Toggle details"
                                disabled={!isNonReqByLaw}
                              >
                                <ChevronDown
                                  className={`h-4 w-4 text-muted-foreground transition-transform ${isExpanded ? "rotate-180" : ""}`}
                                />
                              </button>
                            </td>
                          )}
                        </tr>
                        {isExpanded && isNonReqByLaw && (
                          <tr>
                            <td colSpan={5}>
                              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 rounded-radius border bg-background/40 p-4 m-2 text-sm text-muted-foreground">
                                {MATERIALITY_ASSESSMENT_CRITERIA.map(
                                  (criterion) => {
                                    const fieldKey =
                                      CRITERION_FIELD_MAP[criterion.key];
                                    if (!fieldKey) return null;
                                    return (
                                      <div
                                        key={`${category.key}-${criterion.key}`}
                                        className="space-y-2"
                                      >
                                        <SelectField
                                          id={`${category.key}-${criterion.key}`}
                                          label={t(criterion.label)}
                                          placeholder={`Select ${t(criterion.label)}`}
                                          value={
                                            typeof detail[fieldKey] ===
                                              "number" ||
                                            typeof detail[fieldKey] === "string"
                                              ? String(detail[fieldKey])
                                              : detail[fieldKey]
                                          }
                                          onValueChange={(value) =>
                                            updateRowDetail(
                                              category.key,
                                              criterion.key,
                                              Number(value),
                                            )
                                          }
                                          options={criterion.options.map(
                                            (opt) => ({
                                              key: opt.score,
                                              label: `${t(opt.label)}`,
                                            }),
                                          )}
                                          error={getDetailFieldError(
                                            category.key,
                                            fieldKey,
                                          )}
                                          disabled={form_disabled}
                                          required
                                        />
                                      </div>
                                    );
                                  },
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </OnboardingWizardShell>

        {/* {isFromDashboard && (
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("Back")}
          </Button>
        )} */}
      </div>
    );
  },
);

export default NxmapForm;
